import React, { useState, useCallback, useEffect } from "react";
import axiosInstance from "../api/axiosInstance";
import { useToast } from "../context/ToastContext";
import { humanizeError } from "../utils/userFriendlyError";
import { updateHouseholdAPI } from "../api/updateHouseholdAPI";
import {
  formatSSNInput,
  formatPhoneInput,
  formatZipInput,
  validateSSNDigits,
  validatePhoneDigits,
  validateZipDigits,
  validateEmail
} from "../utils/validationHelpers";
import StateCitySelect from "./StateCitySelect";
import ExistingAddressPicker from "./ExistingAddressPicker";
import { US_STATES } from "../data/usStatesAndCities";
import DateInput from "./DateInput";
import { formatDateForInput } from "../utils/formatters";
import {
  getDatePickerBoundsForConstraint,
  normalizeToYmd,
  validateDobYmd,
  validatePastOnlyYmd,
} from "../utils/dateFieldConstraints";
import {
  EMPLOYMENT_STATUS_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS,
  TITLE_OPTIONS,
  shouldDisableEmploymentDetails,
  normalizeEmploymentInfoForStatus,
} from "../constants/membersConstants";
import { COUNTRY_OPTIONS } from "../constants/projectConstants";
import {
  getBrokerageFlags,
  showMaritalStatus,
} from "../utils/brokerageFields";
import {
  isBusinessOwnerOrControlPerson,
  isSimpleIraOwner,
  isDeceasedOrDecedent,
  isCustodialMinorOwner,
} from "../utils/memberLinks";
import { getWaivedMemberFields } from "../utils/memberAccountRoles";
import {
  NON_EDITABLE_PENDING_FIELD_MESSAGES,
  isNonEditablePendingField,
} from "../constants/nonEditablePendingFields";
import { AlertTriangle } from "lucide-react";


/**
 * Safely parse member.personal_info — handles both pre-parsed object
 * and raw JSON string (the API can return either depending on context).
 */
const safeParsePersonalInfo = (member) => {
  const pi = member.personal_info;
  if (!pi) return {};
  if (typeof pi === 'string') {
    try { return JSON.parse(pi); } catch { return {}; }
  }
  return pi;
};

/**
 * id_info is a TOP-LEVEL array on the member object (member.id_info),
 * not nested inside personal_info. Return the first entry or {}.
 */
const parseIdInfo = (member) => {
  const raw = member.id_info;
  if (Array.isArray(raw)) return raw[0] || {};
  if (raw && typeof raw === 'object') return raw;
  return {};
};

/**
 * Return the full id_info array so we preserve entries [1..n] on save.
 */
const parseIdInfoArray = (member) => {
  const raw = member.id_info;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return [raw];
  return [];
};

function MissingMemberFieldModal({
  isOpen,
  onClose,
  selectedField,
  memberId,
  member,
  household,
  brokerageFlags: brokerageFlagsProp,
  onHouseholdUpdate,
  clientToken,
  household_id,
  onSaveAndNext,
}) {
  const { toast } = useToast();
  const brokerageFlags = brokerageFlagsProp ?? getBrokerageFlags(household);
  const waivedFields = getWaivedMemberFields(
    household,
    member?.id,
    brokerageFlags,
  );
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [addressPickerKey, setAddressPickerKey] = useState(0);


  const handleFieldChange = useCallback((fieldPath, value) => {
    let sanitizedValue = value;
    
    // Pattern-based sanitation
    if (fieldPath.includes('ssn_or_tin') || fieldPath === 'tin') {
      sanitizedValue = formatSSNInput(value);
    } else if (fieldPath.includes('phone')) {
      sanitizedValue = formatPhoneInput(value);
    } else if (fieldPath.includes('postal_code')) {
      sanitizedValue = formatZipInput(value);
    }

    setFormData(prev => {
      const newData = { ...prev };
      const keys = fieldPath.split('.');
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = sanitizedValue;
      return newData;
    });
  }, []);

  const getFormValue = useCallback((fieldPath, fallback) => {
    if (!fieldPath) return fallback;
    const keys = fieldPath.split('.');
    let current = formData;
    for (const key of keys) {
      if (current && Object.prototype.hasOwnProperty.call(current, key)) {
        current = current[key];
      } else {
        return fallback;
      }
    }
    return current !== undefined ? current : fallback;
  }, [formData]);

  const buildPersonalInfoUpdate = (member, personalUpdates) => {
    const basePersonal = safeParsePersonalInfo(member);
    const updatedPersonal = {
      ...basePersonal,
    };

    if (personalUpdates) {
      Object.keys(personalUpdates).forEach(key => {
        if (key !== "address") {
          updatedPersonal[key] = personalUpdates[key];
        }
      });
    }

    if (personalUpdates?.address) {
      const existingAddresses = Array.isArray(basePersonal.address) ? [...basePersonal.address] : [];
      const updatedAddresses = [...existingAddresses];

      Object.keys(personalUpdates.address).forEach(typeKey => {
        const addressUpdates = personalUpdates.address[typeKey] || {};
        const existingIndex = updatedAddresses.findIndex(addr => addr?.type === typeKey);
        const baseAddress = existingIndex >= 0
          ? { ...updatedAddresses[existingIndex] }
          : { type: typeKey };

        const mergedAddress = { ...baseAddress, ...addressUpdates };
        if (existingIndex >= 0) {
          updatedAddresses[existingIndex] = mergedAddress;
        } else {
          updatedAddresses.push(mergedAddress);
        }
      });

      updatedPersonal.address = updatedAddresses;
    }

    if (updatedPersonal.dob) {
      updatedPersonal.dob = formatDateForInput(updatedPersonal.dob);
    }

    if (updatedPersonal.date_of_death) {
      updatedPersonal.date_of_death = formatDateForInput(updatedPersonal.date_of_death);
    }

    // The UI displays "United States" but the backend expects "USA".
    // Force "USA" if blank or specifically "United States" — but never
    // fabricate citizenship for deceased/decedent members (not mandatory).
    const currentCitizenship = String(updatedPersonal.country_of_citizenship || "").trim();
    if (currentCitizenship === "United States") {
      updatedPersonal.country_of_citizenship = "USA";
    } else if (
      currentCitizenship === "" &&
      !isDeceasedOrDecedent(member, household?.accounts)
    ) {
      updatedPersonal.country_of_citizenship = "USA";
    }

    return updatedPersonal;
  };

  const buildEmploymentInfoUpdate = (member, employmentUpdates) => {
    const baseEmployment = member.employment_info || {};
    const updatedEmployment = { ...baseEmployment };

    if (employmentUpdates) {
      Object.keys(employmentUpdates).forEach(key => {
        if (key !== "address") {
          updatedEmployment[key] = employmentUpdates[key];
        }
      });
    }

    if (employmentUpdates && Object.prototype.hasOwnProperty.call(employmentUpdates, "address")) {
      if (employmentUpdates.address == null) {
        updatedEmployment.address = null;
      } else {
        const baseAddress = baseEmployment.address || {};
        updatedEmployment.address = { ...baseAddress, ...employmentUpdates.address };
      }
    }

    return normalizeEmploymentInfoForStatus(updatedEmployment);
  };

  const handleSave = async () => {
    if (!household || !memberId || !selectedField || !member) return;

    if (selectedField === "personal_info") {
      const basePersonal = safeParsePersonalInfo(member);
      const dobCandidate =
        formData.dob !== undefined && formData.dob !== null && formData.dob !== ""
          ? formData.dob
          : basePersonal.dob;
      if (dobCandidate) {
        const msg = validateDobYmd(
          normalizeToYmd(dobCandidate),
          member.is_primary === true &&
            !isCustodialMinorOwner(household?.accounts, member.id),
        );
        if (msg) {
          toast.error(msg);
          return;
        }
      }

      const dateOfDeathCandidate =
        formData.date_of_death !== undefined &&
        formData.date_of_death !== null &&
        formData.date_of_death !== ""
          ? formData.date_of_death
          : basePersonal.date_of_death;
      if (dateOfDeathCandidate) {
        const dodMsg = validatePastOnlyYmd(
          normalizeToYmd(dateOfDeathCandidate),
          "Date of death",
        );
        if (dodMsg) {
          toast.error(dodMsg);
          return;
        }
      }
    }

    if (selectedField === "personal_info") {
      const ssnValue = String(getFormValue("ssn_or_tin", "") || "").trim();
      if (ssnValue && !validateSSNDigits(ssnValue)) {
        toast.error("Please enter a valid 9-digit SSN/TIN.");
        return;
      }
    }

    if (selectedField === "email") {
      const emailValue = String(getFormValue("email", "") || "").trim();
      if (emailValue && !validateEmail(emailValue)) {
        toast.error("Please enter a valid email address.");
        return;
      }
    }

    if (selectedField === "phone") {
      const phoneValue = getFormValue("address.phone", "");
      if (String(phoneValue || "").trim() && !validatePhoneDigits(phoneValue)) {
        toast.error("Please enter a valid 10-digit phone number.");
        return;
      }
    }

    setSaving(true);
    try {
      const update = {};

      if (selectedField === 'personal_info') {
        const personalUpdates = {};
        const deceasedOnly = isDeceasedOrDecedent(member, household?.accounts);
        const mandatoryFields = deceasedOnly
          ? ['first_name', 'last_name', 'ssn_or_tin', 'dob']
          : ['title', 'first_name', 'last_name', ...(showMaritalStatus(brokerageFlags) ? ['marital_status'] : []), 'ssn_or_tin', 'dob', 'country_of_citizenship'];
        if (!deceasedOnly && member.is_primary === false) {
          mandatoryFields.push('relationship');
        }
        const basePersonal = safeParsePersonalInfo(member);

        mandatoryFields.forEach(field => {
          const formValue = formData[field];
          if (formValue !== undefined && formValue !== null && formValue !== '') {
            // User entered a value - use it
            personalUpdates[field] = formValue;
          } else if (basePersonal[field] !== null && basePersonal[field] !== undefined && basePersonal[field] !== '') {
            // Field not touched but has existing value - keep it
            personalUpdates[field] = basePersonal[field];
          }
        });

        if (formData.is_deceased !== undefined && formData.is_deceased !== null) {
          personalUpdates.is_deceased = formData.is_deceased === true;
        } else if (basePersonal.is_deceased !== undefined && basePersonal.is_deceased !== null) {
          personalUpdates.is_deceased = basePersonal.is_deceased === true;
        }

        const dateOfDeathValue =
          formData.date_of_death !== undefined &&
          formData.date_of_death !== null &&
          formData.date_of_death !== ''
            ? formData.date_of_death
            : basePersonal.date_of_death;
        if (dateOfDeathValue !== undefined && dateOfDeathValue !== null && dateOfDeathValue !== '') {
          personalUpdates.date_of_death = dateOfDeathValue;
        }

        if (Object.keys(personalUpdates).length > 0) {
          update.personal_info = buildPersonalInfoUpdate(member, personalUpdates);
        }
      }

      if (selectedField === 'address') {
        const personalUpdates = { address: {} };
        const addressFields = ['address_line_1', 'address_line_2', 'city', 'province', 'postal_code', 'country'];

        // Use safe parser so personal_info JSON strings are handled
        const parsedPI = safeParsePersonalInfo(member);
        const existingAddresses = Array.isArray(parsedPI.address) ? parsedPI.address : [];
        const homeAddressIndex = existingAddresses.findIndex(addr => addr?.type === 'home');
        const existingHomeAddress = homeAddressIndex >= 0 ? existingAddresses[homeAddressIndex] : null;

        const addressUpdates = {};
        addressFields.forEach(field => {
          const value = getFormValue(`address.${field}`, '');
          if (value !== undefined && value !== null && value !== '') {
            addressUpdates[field] = value;
          } else if (existingHomeAddress && existingHomeAddress[field]) {
            // Keep existing value if field not touched
            addressUpdates[field] = existingHomeAddress[field];
          }
        });
        
        // Ensure country defaults to "USA" if blank or "United States"
        if (!addressUpdates.country || addressUpdates.country.trim() === "" || addressUpdates.country === "United States") {
          addressUpdates.country = "USA";
        }

        // Include type
        addressUpdates.type = 'home';

        if (Object.keys(addressUpdates).length > 0) {
          personalUpdates.address.home = addressUpdates;
          update.personal_info = buildPersonalInfoUpdate(member, personalUpdates);
        }
      }

      if (selectedField === 'email') {
        const basePersonal = safeParsePersonalInfo(member);
        const emailValue = getFormValue('email', '');
        if (emailValue !== undefined && emailValue !== null && emailValue !== '') {
          update.personal_info = { ...basePersonal, email: emailValue };
        }
      }

      if (selectedField === 'phone') {
        const basePersonal = safeParsePersonalInfo(member);
        const phoneValue = getFormValue('address.phone', '');
        if (phoneValue !== undefined && phoneValue !== null && phoneValue !== '') {
          const existingAddresses = Array.isArray(basePersonal.address)
            ? basePersonal.address.map((addr) => ({ ...addr }))
            : [];
          const homeIndex = existingAddresses.findIndex(addr => addr?.type === 'home');
          if (homeIndex >= 0) {
            existingAddresses[homeIndex] = { ...existingAddresses[homeIndex], phone: phoneValue };
          } else {
            existingAddresses.push({ type: 'home', phone: phoneValue });
          }
          update.personal_info = { ...basePersonal, address: existingAddresses };
        }
      }

      if (selectedField === 'id_info') {
        const idUpdates = {
          type: getFormValue('id_info.type', ''),
          number: getFormValue('id_info.number', ''),
          issue_state: getFormValue('id_info.issue_state', ''),
          issuance_date: getFormValue('id_info.issuance_date', ''),
          expiration_date: getFormValue('id_info.expiration_date', '')
        };

        // id_info lives as a top-level member field (not inside personal_info)
        const existingIdInfoArray = parseIdInfoArray(member);
        const existingEntry = parseIdInfo(member);

        // Merge: form values win; fall back to whatever already exists
        const mergedEntry = { ...existingEntry };
        Object.keys(idUpdates).forEach(key => {
          if (idUpdates[key] !== undefined && idUpdates[key] !== null && idUpdates[key] !== '') {
            mergedEntry[key] = idUpdates[key];
          }
        });

        if (Object.keys(mergedEntry).length > 0) {
          // Save as top-level id_info array, preserving any additional entries
          update.id_info = [mergedEntry, ...existingIdInfoArray.slice(1)];
        }
      }

      if (selectedField === 'employment_info') {
        const employmentUpdates = {};
        const baseEmployment = member.employment_info || {};
        
        // Handle status — form value wins; otherwise keep existing ONLY if it's
        // a valid string (legacy records can have boolean `true` from Python).
        const statusValue = formData.status;
        if (statusValue !== undefined && statusValue !== null && statusValue !== '') {
          employmentUpdates.status = statusValue;
        } else if (baseEmployment.status && typeof baseEmployment.status === 'string') {
          employmentUpdates.status = baseEmployment.status;
        }

        // Handle occupation, industry, and employer
        const occupationValue = formData.occupation;
        if (occupationValue !== undefined && occupationValue !== null && occupationValue !== '') {
          employmentUpdates.occupation = occupationValue;
        } else if (baseEmployment.occupation !== null && baseEmployment.occupation !== undefined && baseEmployment.occupation !== '') {
          // Keep existing value if not touched
          employmentUpdates.occupation = baseEmployment.occupation;
        }

        // industry removed from UI — no longer collected
        // const industryValue = formData.industry;
        // if (industryValue !== undefined && industryValue !== null && industryValue !== '') {
        //   employmentUpdates.industry = industryValue;
        // } else if (baseEmployment.industry !== null && baseEmployment.industry !== undefined && baseEmployment.industry !== '') {
        //   // Keep existing value if not touched
        //   employmentUpdates.industry = baseEmployment.industry;
        // }

        const employerValue = formData.employer;
        if (employerValue !== undefined && employerValue !== null && employerValue !== '') {
          employmentUpdates.employer = employerValue;
        } else if (baseEmployment.employer !== null && baseEmployment.employer !== undefined && baseEmployment.employer !== '') {
          // Keep existing value if not touched
          employmentUpdates.employer = baseEmployment.employer;
        }

        const titleValue = formData.title;
        if (titleValue !== undefined && titleValue !== null && titleValue !== '') {
          employmentUpdates.title = titleValue;
        } else if (baseEmployment.title !== null && baseEmployment.title !== undefined && baseEmployment.title !== '') {
          employmentUpdates.title = baseEmployment.title;
        }

        const tinValue = formData.tin;
        if (tinValue !== undefined && tinValue !== null && tinValue !== '') {
          employmentUpdates.tin = tinValue;
        } else if (baseEmployment.tin !== null && baseEmployment.tin !== undefined && baseEmployment.tin !== '') {
          employmentUpdates.tin = baseEmployment.tin;
        }

        // Handle address
        const addressFields = ['address_line_1', 'address_line_2', 'city', 'province', 'postal_code', 'country', 'phone'];
        const addressUpdates = {};
        let hasAddressFields = false;

        addressFields.forEach(field => {
          const formValue = getFormValue(`employment_address.${field}`, '');
          if (formValue !== undefined && formValue !== null && formValue !== '') {
            // User entered a value
            addressUpdates[field] = formValue;
            hasAddressFields = true;
          } else if (baseEmployment.address?.[field]) {
            // Keep existing value if field not touched
            addressUpdates[field] = baseEmployment.address[field];
            hasAddressFields = true;
          }
        });

        if (hasAddressFields) {
          addressUpdates.type = 'work';
          // Ensure country defaults to "USA" if blank or "United States"
          if (!addressUpdates.country || addressUpdates.country.trim() === "" || addressUpdates.country === "United States") {
            addressUpdates.country = "USA";
          }
          employmentUpdates.address = addressUpdates;
        }

        if (Object.keys(employmentUpdates).length > 0) {
          update.employment_info = buildEmploymentInfoUpdate(member, employmentUpdates);
        }
      }

      if (Object.keys(update).length === 0) {
        if (onSaveAndNext) {
          onSaveAndNext();
        } else {
          onClose();
        }
      } else if (Object.keys(update).length > 0) {
        // Use client-side API if clientToken and household_id are provided
        if (clientToken && household_id) {
          const payload = {
            household_members: {
              [memberId]: update
            }
          };
          const result = await updateHouseholdAPI(household_id, clientToken, payload);
          
          if (result.success) {
            if (onSaveAndNext) {
              onSaveAndNext();
            } else {
              onClose();
            }
            if (onHouseholdUpdate) {
              onHouseholdUpdate(null);
            }
          } else {
            toast.error(result.error || "Failed to update member.");
          }
        } else {
          // Fallback to advisor-side API
          const cleanUpdate = JSON.parse(JSON.stringify(update));
          // Strip non-digits from SSN
          if (cleanUpdate.personal_info?.ssn_or_tin) {
            cleanUpdate.personal_info.ssn_or_tin = cleanUpdate.personal_info.ssn_or_tin.replace(/\D/g, '');
          }
          // personal_info.address is an array — sanitize phone on every entry
          if (Array.isArray(cleanUpdate.personal_info?.address)) {
            cleanUpdate.personal_info.address = cleanUpdate.personal_info.address.map(addr =>
              addr?.phone ? { ...addr, phone: addr.phone.replace(/\D/g, '') } : addr
            );
          }
          // employment address is a plain object
          if (cleanUpdate.employment_info?.address?.phone) {
            cleanUpdate.employment_info.address.phone = cleanUpdate.employment_info.address.phone.replace(/\D/g, '');
          }

          const response = await axiosInstance.put(`/households/${household.id}/members/bulk`, {
            members: [{
              member_id: memberId,
              update: cleanUpdate
            }]
          });

          if (response.data?.failed?.length) {
            toast.error(
              `Failed to update member: ${response.data.failed[0].error || "Unknown error"}`,
            );
          } else {
            if (onSaveAndNext) {
              onSaveAndNext();
            } else {
              onClose();
            }
            if (onHouseholdUpdate) {
              onHouseholdUpdate(null);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error(humanizeError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({});
    onClose();
  };

  if (!isOpen || !selectedField || !member) return null;

  const isNonEditable = isNonEditablePendingField(selectedField);

  const getFieldTitle = () => {
    if (selectedField === 'personal_info') return 'Personal Details';
    if (selectedField === 'address') return 'Address';
    if (selectedField === 'email') return 'Email';
    if (selectedField === 'phone') return 'Phone';
    if (selectedField === 'id_info') return 'Identification Info';
    if (selectedField === 'employment_info') return 'Employment Information';
    if (selectedField === 'minor_owns_non_custodial_account') return 'Minor Owner Not Allowed';
    return selectedField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Safely parse personal_info (may arrive as JSON string or plain object)
  const parsedPersonalInfo = safeParsePersonalInfo(member);
  // Get existing home address if any
  const existingHomeAddress = Array.isArray(parsedPersonalInfo.address)
    ? parsedPersonalInfo.address.find(addr => addr?.type === 'home')
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            {getFieldTitle()} - {parsedPersonalInfo.first_name || 'Member'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-6 py-4">
          {selectedField === 'personal_info' && (
            <div className="space-y-4">
              {(() => {
                const personalInfo = parsedPersonalInfo;
                const deceasedOnly = isDeceasedOrDecedent(member, household?.accounts);
                const mandatoryFields = deceasedOnly
                  ? [
                      { key: 'first_name', label: 'First Name', type: 'text' },
                      { key: 'last_name', label: 'Last Name', type: 'text' },
                      { key: 'ssn_or_tin', label: 'SSN or TIN', type: 'text' },
                      { key: 'dob', label: 'Date of Birth', type: 'date' },
                    ]
                  : [
                      { key: 'title', label: 'Title', type: 'select', options: TITLE_OPTIONS },
                      { key: 'first_name', label: 'First Name', type: 'text' },
                      { key: 'last_name', label: 'Last Name', type: 'text' },
                      ...(showMaritalStatus(brokerageFlags) ? [{ key: 'marital_status', label: 'Marital Status', type: 'select', options: MARITAL_STATUS_OPTIONS }] : []),
                      ...(member.is_primary === false ? [{ key: 'relationship', label: 'Relationship to Primary Member', type: 'select', options: RELATIONSHIP_OPTIONS }] : []),
                      { key: 'ssn_or_tin', label: 'SSN or TIN', type: 'text' },
                      { key: 'dob', label: 'Date of Birth', type: 'date' },
                      { key: 'country_of_citizenship', label: 'Country of Citizenship', type: 'select', options: COUNTRY_OPTIONS }
                    ].filter(field => !waivedFields.has(field.key));

                // Only show fields that are null/undefined
                const nullFields = mandatoryFields.filter(field =>
                  personalInfo[field.key] === null || personalInfo[field.key] === undefined || personalInfo[field.key] === ''
                );

                const isDeceasedFormValue = getFormValue('is_deceased', personalInfo.is_deceased === true);
                const showDeceasedCheckbox = personalInfo.is_deceased !== true;
                const showDateOfDeath =
                  (isDeceasedFormValue === true || deceasedOnly) &&
                  (personalInfo.date_of_death === null ||
                    personalInfo.date_of_death === undefined ||
                    personalInfo.date_of_death === '');

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {nullFields.map((field) => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                        {field.type === 'select' ? (
                          <select
                            value={getFormValue(field.key, field.key === 'country_of_citizenship' ? 'United States' : '')}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select {field.label}</option>
                            {field.options?.map((option) => {
                              const value = typeof option === 'object' ? option.value : option;
                              const label = typeof option === 'object' ? option.label : option;
                              return (
                                <option key={value} value={value}>{label}</option>
                              );
                            })}
                          </select>
                        ) : field.type === 'date' ? (
                          <DateInput
                            value={getFormValue(field.key, '')}
                            onChange={(val) => handleFieldChange(field.key, val)}
                            placeholder="mm-dd-yyyy"
                            {...(field.key === "dob"
                              ? getDatePickerBoundsForConstraint(
                                  member.is_primary &&
                                    !isCustodialMinorOwner(household?.accounts, member.id)
                                    ? "dob_primary"
                                    : "dob_non_primary",
                                )
                              : {})}
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={getFormValue(field.key, field.key === 'country_of_citizenship' ? 'USA' : '')}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                              field.key === 'ssn_or_tin' && getFormValue('ssn_or_tin', '') && !validateSSNDigits(getFormValue('ssn_or_tin', ''))
                                ? 'bg-red-50/30 border-red-200'
                                : 'border-gray-300'
                            }`}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    ))}
                    {showDeceasedCheckbox && (
                      <div key="is_deceased" className="flex items-center min-h-[38px]">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={isDeceasedFormValue === true}
                            onChange={(e) => handleFieldChange('is_deceased', e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Deceased</span>
                        </label>
                      </div>
                    )}
                    {showDateOfDeath && (
                      <div key="date_of_death">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date of Death</label>
                        <DateInput
                          value={getFormValue('date_of_death', '')}
                          onChange={(val) => handleFieldChange('date_of_death', val)}
                          placeholder="mm-dd-yyyy"
                          {...getDatePickerBoundsForConstraint("past_only")}
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedField === 'address' && (
            <div className="space-y-4">
              <ExistingAddressPicker
                household={household}
                excludeMemberId={memberId}
                getField={(k) => getFormValue(`address.${k}`, existingHomeAddress?.[k])}
                setField={(k, v) => handleFieldChange(`address.${k}`, v)}
                onAfterSelect={() => setAddressPickerKey((k) => k + 1)}
              />
              {(() => {
                const mandatoryAddressFields = [
                  { key: 'address_line_1', label: 'Address Line 1' },
                  { key: 'city', label: 'City' },
                  { key: 'province', label: 'Province/State' },
                  { key: 'postal_code', label: 'Postal Code' },
                  { key: 'country', label: 'Country' }
                ];

                const anyMandatoryMissing = mandatoryAddressFields.some(f => !existingHomeAddress?.[f.key]);

                if (!anyMandatoryMissing) return null;

                // Any mandatory subfield blank -> show the whole address group,
                // prefilled from existing values (mirrors the client AddressSection).
                const addressLineFields = [
                  { key: 'address_line_1', label: 'Address Line 1' },
                  { key: 'address_line_2', label: 'Address Line 2', optional: true },
                ];
                const bottomFields = [
                  { key: 'postal_code', label: 'Postal Code' },
                ];

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {addressLineFields.map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                          <input
                            type="text"
                            value={getFormValue(`address.${field.key}`, existingHomeAddress?.[field.key] || '')}
                            onChange={(e) => handleFieldChange(`address.${field.key}`, e.target.value)}
                            className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm border-gray-300"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 mb-4">
                      <StateCitySelect
                        key={addressPickerKey}
                        countryValue={getFormValue('address.country', existingHomeAddress?.country || 'USA')}
                        stateValue={getFormValue('address.province', existingHomeAddress?.province || '')}
                        cityValue={getFormValue('address.city', existingHomeAddress?.city || '')}
                        onCountryChange={(val) => handleFieldChange('address.country', val)}
                        onStateChange={(val) => handleFieldChange('address.province', val)}
                        onCityChange={(val) => handleFieldChange('address.city', val)}
                        isEditing={true}
                        stateLabel="State"
                        cityLabel="City"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {bottomFields.map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                          <input
                            type={field.key === 'phone' ? 'tel' : 'text'}
                            value={getFormValue(`address.${field.key}`, existingHomeAddress?.[field.key] || '')}
                            onChange={(e) => handleFieldChange(`address.${field.key}`, e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                              (field.key === 'phone' && getFormValue('address.phone', '') && !validatePhoneDigits(getFormValue('address.phone', ''))) ||
                              (field.key === 'postal_code' && getFormValue('address.postal_code', '') && !validateZipDigits(getFormValue('address.postal_code', '')))
                                ? 'bg-red-50/30 border-red-200'
                                : 'border-gray-300'
                            }`}
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {selectedField === 'email' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={getFormValue('email', parsedPersonalInfo.email || '')}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                    getFormValue('email', '') && !validateEmail(getFormValue('email', ''))
                      ? 'bg-red-50/30 border-red-200'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter email"
                />
              </div>
            </div>
          )}

          {selectedField === 'phone' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={getFormValue('address.phone', existingHomeAddress?.phone || '')}
                  onChange={(e) => handleFieldChange('address.phone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                    getFormValue('address.phone', '') && !validatePhoneDigits(getFormValue('address.phone', ''))
                      ? 'bg-red-50/30 border-red-200'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter phone"
                />
              </div>
            </div>
          )}

          {selectedField === 'id_info' && (
            <div className="space-y-4">
              {(() => {
                // Use robust helper — handles JSON strings, plain objects, arrays
                const idInfo = parseIdInfo(member);
                const idFields = [
                  { key: 'type', label: 'ID Type', type: 'select', options: ["Driver's License", "Passport", "State ID", "Other"] },
                  { key: 'number', label: 'ID Number', type: 'text' },
                  { key: 'issue_state', label: 'Issuing State', type: 'text' },
                  { key: 'issuance_date', label: 'Issuance Date', type: 'date' },
                  { key: 'expiration_date', label: 'Expiration Date', type: 'date' }
                ];

                const missingFields = idFields.filter(f => !idInfo[f.key]);

                if (missingFields.length === 0) return null;

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {missingFields.map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                        {field.key === 'issue_state' ? (
                          <select
                            value={getFormValue(`id_info.${field.key}`, idInfo[field.key] || '')}
                            onChange={(e) => handleFieldChange(`id_info.${field.key}`, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select State</option>
                            {US_STATES.map(s => <option key={s.abbreviation} value={s.abbreviation}>{s.name} ({s.abbreviation})</option>)}
                          </select>
                        ) : field.type === 'select' ? (
                          <select
                            value={getFormValue(`id_info.${field.key}`, idInfo[field.key] || '')}
                            onChange={(e) => handleFieldChange(`id_info.${field.key}`, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select {field.label}</option>
                            {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        ) : field.type === 'date' ? (
                          <DateInput
                            value={getFormValue(`id_info.${field.key}`, idInfo[field.key] || '')}
                            onChange={(val) => handleFieldChange(`id_info.${field.key}`, val)}
                            placeholder="mm-dd-yyyy"
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={getFormValue(`id_info.${field.key}`, idInfo[field.key] || '')}
                            onChange={(e) => handleFieldChange(`id_info.${field.key}`, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {selectedField === 'employment_info' && (
            <div className="space-y-4">
              {(() => {
                const ei = member.employment_info || {};
                // A valid status must be a non-empty string.
                // Legacy records store status as boolean `true` (Python True) — treat that as missing too.
                const selectedStatus = getFormValue(
                  "status",
                  typeof ei.status === "string" ? ei.status : "",
                );
                const hideEmploymentDetailFields = shouldDisableEmploymentDetails(selectedStatus);

                const statusField = {
                  key: 'status',
                  label: 'Employment Status',
                  type: 'select',
                  options: EMPLOYMENT_STATUS_OPTIONS
                };

                const needsTitle = isBusinessOwnerOrControlPerson(
                  household?.accounts,
                  memberId,
                );
                const needsTin = isSimpleIraOwner(household?.accounts, memberId);

                const detailFieldDefs = hideEmploymentDetailFields
                  ? []
                  : [
                      { key: "occupation", label: "Occupation", type: "text" },
                      // industry removed from UI — commented out for now
                      // { key: "industry", label: "Industry", type: "text" },
                      { key: "employer", label: "Employer", type: "text" },
                      ...(needsTitle
                        ? [{ key: "title", label: "Job Title", type: "text" }]
                        : []),
                      ...(needsTin
                        ? [{ key: "tin", label: "Employer TIN/EIN", type: "text" }]
                        : []),
                    ];

                const missingDetailFields = detailFieldDefs.filter((f) => !ei[f.key]);
                const fieldsToRender = [statusField, ...missingDetailFields];

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fieldsToRender.map(field => (
                      <div key={field.key}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                        {field.type === 'select' ? (
                          <select
                            value={getFormValue(
                              field.key,
                              field.key === "status"
                                ? (typeof ei.status === "string" && ei.status
                                    ? ei.status
                                    : "")
                                : "",
                            )}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          >
                            <option value="">Select value</option>
                            {field.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={getFormValue(field.key, ei[field.key] || '')}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            placeholder={`Enter ${field.label.toLowerCase()}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              
              {(() => {
                const selectedStatus = getFormValue(
                  "status",
                  typeof member.employment_info?.status === "string"
                    ? member.employment_info.status
                    : "",
                );
                if (shouldDisableEmploymentDetails(selectedStatus)) return null;

                const mandatoryAddressFields = [
                  { key: 'address_line_1', label: 'Address Line 1' },
                  { key: 'city', label: 'City' },
                  { key: 'province', label: 'Province/State' },
                  { key: 'postal_code', label: 'Postal Code' },
                  { key: 'country', label: 'Country' },
                  { key: 'phone', label: 'Phone' }
                ];

                const anyMandatoryMissing = mandatoryAddressFields.some(f => !member.employment_info?.address?.[f.key]);

                if (!anyMandatoryMissing) return null;

                // Any mandatory subfield blank -> show the whole address group,
                // prefilled from existing values (mirrors the client AddressSection).
                const addressLineFields = [
                  { key: 'address_line_1', label: 'Address Line 1' },
                  { key: 'address_line_2', label: 'Address Line 2', optional: true },
                ];
                const bottomFields = [
                  { key: 'postal_code', label: 'Postal Code' },
                  { key: 'phone', label: 'Phone' },
                ];

                return (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Work Address</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {addressLineFields.map(field => (
                          <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                            <input
                              type="text"
                              value={getFormValue(`employment_address.${field.key}`, member.employment_info?.address?.[field.key] || '')}
                              onChange={(e) => handleFieldChange(`employment_address.${field.key}`, e.target.value)}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm border-gray-300"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="bg-emerald-50/50 p-4 rounded-xl border border-emerald-100 mb-4">
                        <StateCitySelect
                          countryValue={getFormValue('employment_address.country', member.employment_info?.address?.country || 'USA')}
                          stateValue={getFormValue('employment_address.province', member.employment_info?.address?.province || '')}
                          cityValue={getFormValue('employment_address.city', member.employment_info?.address?.city || '')}
                          onCountryChange={(val) => handleFieldChange('employment_address.country', val)}
                          onStateChange={(val) => handleFieldChange('employment_address.province', val)}
                          onCityChange={(val) => handleFieldChange('employment_address.city', val)}
                          isEditing={true}
                          stateLabel="State"
                          cityLabel="City"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {bottomFields.map(field => (
                          <div key={field.key}>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{field.label}</label>
                            <input
                              type={field.key === 'phone' ? 'tel' : 'text'}
                              value={getFormValue(`employment_address.${field.key}`, member.employment_info?.address?.[field.key] || '')}
                              onChange={(e) => handleFieldChange(`employment_address.${field.key}`, e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 text-sm ${
                                (field.key === 'phone' && getFormValue('employment_address.phone', '') && !validatePhoneDigits(getFormValue('employment_address.phone', ''))) ||
                                (field.key === 'postal_code' && getFormValue('employment_address.postal_code', '') && !validateZipDigits(getFormValue('employment_address.postal_code', '')))
                                  ? 'bg-red-50/30 border-red-200'
                                  : 'border-gray-300'
                              }`}
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {isNonEditable && (
            <div className="flex items-start gap-3 border border-amber-200 bg-amber-50 rounded-lg p-4">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                {NON_EDITABLE_PENDING_FIELD_MESSAGES[selectedField]}
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {isNonEditable ? "Close" : "Cancel"}
          </button>
          {!isNonEditable && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Save & Next
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default MissingMemberFieldModal;
